import type { ArchetypeId, ChallengeTemplate, ChestDef, ChestId, GearItem, GearRarity, GearSlot, HeroId, ProductId, Profile, QuestCompletion, QuestView, RunSubmission, SkillId, SkinId } from '@holdline/sim';

export interface ChallengeView extends ChallengeTemplate {
  goalWaves: number;
  done: boolean;
}

export interface ChallengeResult {
  week: string;
  score: number;
  waves: number;
  goal: number;
  reached: boolean;
  completed: boolean;
  alreadyDone: boolean;
  medals: number;
  streak: { count: number; medals: number } | null;
  profile: Profile;
}

export type BoardPeriod = 'day' | 'week' | 'all';

export interface ReferralInfo {
  /** t.me link with the invite parameter, or null when the server has no bot (local dev). */
  link: string | null;
  invites: number;
  by: number | null;
  claimed: boolean;
  goalWaves: number;
  medalsEach: number;
  maxInvites: number;
}

export interface BoardInfo {
  period: BoardPeriod;
  from: string;
  to: string;
  players: number;
  leaderboard: BoardEntry[];
  me: { name: string; best: number | null; rank: number | null } | null;
}

export interface BoardEntry {
  userId: number;
  name: string;
  score: number;
  waves: number;
  me: boolean;
}

export interface DailyInfo {
  day: string;
  seedLabel: string;
  botUsername: string | null;
  players: number;
  leaderboard: BoardEntry[];
  me: { name: string; best: number | null } | null;
}

export interface RunRewardsResult {
  copies: Partial<Record<ArchetypeId, number>>;
  heroXp: number;
  medals?: number;
  cardLevelUps: { arch: ArchetypeId; level: number }[];
  heroLevelUp: number | null;
  gear: GearItem | null;
  gearLost: boolean;
}

export interface SubmitResult {
  day: string;
  score: number;
  waves: number;
  best: number;
  rank: number;
  players: number;
  leaderboard: BoardEntry[];
  /** null when the daily reward cap is spent; the run still counts for the board. */
  rewards: RunRewardsResult | null;
  /** Set on the first accepted run of the day: the streak length and the medals it paid. */
  streak: { count: number; medals: number } | null;
  /** Set once, when an invited player clears the referral goal: what they were paid. */
  referral: { medals: number } | null;
  /** Ladder levels only: the star rating, whether this was the first clear and what it paid, and the next level's map. */
  ladder: { n: number; stars: number; firstWin: boolean; medals: number; next: string | null } | null;
  profile: Profile;
  rewardedRunsLeft: number;
  quests: QuestsUpdate;
}

export interface QuestsUpdate {
  completed: QuestCompletion[];
  list: QuestView[];
}

export interface ProfileInfo {
  profile: Profile;
  rewardedRunsLeft: number;
}

export interface ShopInfo {
  day: string;
  starsUsd: number;
  chests: ChestDef[];
  pityEvery: number;
  pityLeft: number;
  copiesPack: { stars: number; copies: number };
  heroXpPack: { stars: number; xp: number };
  starterPack: { stars: number; fullStars: number; copiesEach: number; heroXp: number; medals: number; gear: { slot: GearSlot; rarity: GearRarity; bonus: number } };
  starterAvailable: boolean;
  skins: { id: SkinId; stars: number }[];
  skinsOwned: SkinId[];
  skin: SkinId;
  freeChest: ChestId;
  freeChestAvailable: boolean;
  adult: boolean;
  region: { country: string | null; chestsAllowed: boolean };
  payments: 'telegram' | 'dev' | 'off';
}

/** What a chest or pack gave. Chest fields are optional because packs have no item. */
export interface PurchaseResult {
  product: ProductId;
  arch?: ArchetypeId | null;
  skin?: SkinId;
  copies?: Partial<Record<ArchetypeId, number>>;
  heroXp?: number;
  xp?: number;
  medals?: number;
  gear?: GearItem | null;
  gearLost?: boolean;
  cardLevelUps?: { arch: ArchetypeId; level: number }[];
  heroLevelUp?: number | null;
  pityHit?: boolean;
  pityLeft?: number;
}

export interface InvoiceInfo {
  orderId: number;
  stars: number;
  link: string | null;
  dev: boolean;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`${status} ${code}`);
  }
}

/**
 * Talks to apps/server. Inside Telegram we send initData; outside (local dev) a stable
 * per-browser dev name, which the server accepts only with DEV_AUTH=1.
 */
export class Api {
  constructor(
    private readonly base: string,
    private readonly initData: string,
    private readonly devUser: string | null,
  ) {}

  private headers(): Record<string, string> {
    const h: Record<string, string> = { 'content-type': 'application/json' };
    if (this.initData) h['authorization'] = `tma ${this.initData}`;
    else if (this.devUser) h['x-dev-user'] = this.devUser;
    return h;
  }

  async daily(mapId: string): Promise<DailyInfo> {
    const r = await fetch(`${this.base}/api/daily?map=${encodeURIComponent(mapId)}`, { headers: this.headers() });
    if (!r.ok) throw new ApiError(r.status, 'daily_failed');
    return (await r.json()) as DailyInfo;
  }

  async referral(): Promise<ReferralInfo> {
    const r = await fetch(`${this.base}/api/referral`, { headers: this.headers() });
    if (!r.ok) throw new ApiError(r.status, 'referral_failed');
    return (await r.json()) as ReferralInfo;
  }

  async challenges(): Promise<{ week: string; list: ChallengeView[] }> {
    const r = await fetch(`${this.base}/api/challenges`, { headers: this.headers() });
    if (!r.ok) throw new ApiError(r.status, 'challenges_failed');
    return (await r.json()) as { week: string; list: ChallengeView[] };
  }

  /** A finished challenge run: the server replays it under the challenge's modifiers and pays medals once a week. */
  async submitChallenge(body: RunSubmission & { challengeId: string }): Promise<ChallengeResult> {
    const r = await fetch(`${this.base}/api/challenge/run`, { method: 'POST', headers: this.headers(), body: JSON.stringify(body) });
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(r.status, j.error ?? 'error');
    }
    return (await r.json()) as ChallengeResult;
  }

  async board(mapId: string, period: BoardPeriod): Promise<BoardInfo> {
    const r = await fetch(`${this.base}/api/board?map=${encodeURIComponent(mapId)}&period=${period}`, { headers: this.headers() });
    if (!r.ok) throw new ApiError(r.status, 'board_failed');
    return (await r.json()) as BoardInfo;
  }

  /** Buy one level of a skill tree node with medals; answers with the whole profile. */
  async upgradeSkill(skillId: SkillId): Promise<ProfileInfo & { level: number }> {
    const r = await fetch(`${this.base}/api/skills/upgrade`, { method: 'POST', headers: this.headers(), body: JSON.stringify({ skillId }) });
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(r.status, j.error ?? 'error');
    }
    return (await r.json()) as ProfileInfo & { level: number };
  }

  async profile(): Promise<ProfileInfo> {
    const r = await fetch(`${this.base}/api/profile`, { headers: this.headers() });
    if (!r.ok) throw new ApiError(r.status, 'profile_failed');
    return (await r.json()) as ProfileInfo;
  }

  /** Profile mutations for gear; every call answers with the whole updated profile. */
  async gear(action: 'equip' | 'unequip' | 'scrap' | 'merge', body: { itemId?: number; slot?: GearSlot; rarity?: GearRarity }): Promise<ProfileInfo & { xp?: number; item?: GearItem }> {
    const r = await fetch(`${this.base}/api/gear/${action}`, { method: 'POST', headers: this.headers(), body: JSON.stringify(body) });
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(r.status, j.error ?? 'error');
    }
    return (await r.json()) as ProfileInfo & { xp?: number; item?: GearItem };
  }

  private async post<T>(path: string, body: object): Promise<T> {
    const r = await fetch(`${this.base}${path}`, { method: 'POST', headers: this.headers(), body: JSON.stringify(body) });
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(r.status, j.error ?? 'error');
    }
    return (await r.json()) as T;
  }

  async shop(): Promise<ShopInfo> {
    const r = await fetch(`${this.base}/api/shop`, { headers: this.headers() });
    if (!r.ok) throw new ApiError(r.status, 'shop_failed');
    return (await r.json()) as ShopInfo;
  }

  selectHero(heroId: HeroId): Promise<{ profile: Profile }> {
    return this.post('/api/hero/select', { heroId });
  }

  consent(): Promise<{ profile: Profile }> {
    return this.post('/api/shop/consent', {});
  }

  freeChest(): Promise<{ result: PurchaseResult; profile: Profile; quests: QuestsUpdate }> {
    return this.post('/api/shop/free-chest', {});
  }

  async quests(): Promise<{ day: string; quests: QuestView[] }> {
    const r = await fetch(`${this.base}/api/quests`, { headers: this.headers() });
    if (!r.ok) throw new ApiError(r.status, 'quests_failed');
    return (await r.json()) as { day: string; quests: QuestView[] };
  }

  invoice(product: ProductId, arch?: ArchetypeId, skin?: SkinId): Promise<InvoiceInfo> {
    return this.post('/api/shop/invoice', skin ? { product, skin } : arch ? { product, arch } : { product });
  }

  selectSkin(skin: SkinId): Promise<ProfileInfo & { skin: SkinId }> {
    return this.post('/api/shop/skin', { skin });
  }

  devPay(orderId: number): Promise<{ result: PurchaseResult; profile: Profile }> {
    return this.post('/api/shop/dev-pay', { orderId });
  }

  async order(orderId: number): Promise<{ status: 'pending' | 'paid'; result: PurchaseResult | null; profile: Profile }> {
    const r = await fetch(`${this.base}/api/shop/order/${orderId}`, { headers: this.headers() });
    if (!r.ok) throw new ApiError(r.status, 'order_failed');
    return (await r.json()) as { status: 'pending' | 'paid'; result: PurchaseResult | null; profile: Profile };
  }

  async submit(sub: RunSubmission): Promise<SubmitResult> {
    const r = await fetch(`${this.base}/api/run`, { method: 'POST', headers: this.headers(), body: JSON.stringify(sub) });
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(r.status, j.error ?? 'error');
    }
    return (await r.json()) as SubmitResult;
  }
}

export function devUserName(): string {
  const key = 'holdline.devUser';
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const name = `dev-${Math.random().toString(36).slice(2, 6)}`;
    localStorage.setItem(key, name);
    return name;
  } catch {
    return 'dev-anon';
  }
}
